import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CircularProgress, styled } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { me } from './apis/authApi';
import MeErrorDialog from './components/MeErrorDialog';
import { useAppDispatch, useAppSelector } from './hooks';
import { logout as logoutAction } from './slices/authSlice';
import { setUser } from './slices/userSlice';

const Container = styled('div')(() => {
  return {
    '& .loading': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100vw',
      height: '100vh',
    },
  };
});

const App = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const accessToken = useAppSelector((store) => store.auth.accessToken);
  const expiresIn = useAppSelector((store) => store.auth.expiresIn);
  const email = useAppSelector((store) => store.user.email);
  const [isReady, setIsReady] = useState(false);
  const now = Date.now();

  useEffect(() => {
    if (!accessToken && location.pathname !== '/login') {
      navigate('/login', { replace: true });
    } else if (expiresIn) {
      if (expiresIn > now) {
        if (location.pathname === '/login') {
          navigate('/', { replace: true });
        }
      }
    }
  }, [location, accessToken, expiresIn, now]);

  const {
    data: meData,
    isLoading,
    isSuccess,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const result = await me();
      return result.data;
    },
    enabled: !!accessToken,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (isSuccess && meData) {
      dispatch(setUser(meData.data));
      setIsReady(true);
    }
  }, [isSuccess, meData]);

  const handleLogout = () => {
    dispatch(logoutAction());
    navigate('/login', { replace: true });
  };

  const meErrorMessage =
    error instanceof AxiosError
      ? error.response?.data?.error?.message
      : undefined;

  return (
    <Container className="App">
      {accessToken && isLoading && (
        <div className="loading">
          <CircularProgress color="primary" />
        </div>
      )}
      {accessToken && isError && (
        <MeErrorDialog
          open
          email={email}
          errorMessage={meErrorMessage}
          onLogout={handleLogout}
          onRefresh={() => refetch()}
        />
      )}
      {((!accessToken && location.pathname === '/login') || isReady) && (
        <Outlet />
      )}
    </Container>
  );
};

export default App;
